import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import InlineHelpText from '../../shared/InlineHelpText';
import HelpIcon from '../../shared/HelpIcon';
import InlineWarningText from '../../shared/InlineWarningText';
import SvgBatteryOk from '../../svg/BatteryOk';
import SvgBatteryAlert from '../../svg/BatteryAlert';
import SvgOpenExternal from '../../svg/OpenExternal';
import {
  descriptionText,
  typeText,
  batteryHelpText,
  batteryLowHelpText,
  quickEditAlarmDevicesText,
  quickEditAlertDevicesText,
} from '../../shared/SharedInternationalizations';
import { selectorTypes } from '../../shared/table/TableRow';
import Table from '../../shared/table/Table';
import AccessControl from '../../../AccessControl';
import permissions from '../../../permissionsList';

export function UnconnectedUserDevices(props) {
  const {
    id,
    currentLocation,
    alarmDeviceTypes,
    alertDeviceTypes,
    alarmDeviceList,
    alertDeviceList,
    userObj,
  } = props;

  const [
    showAssignedAlertDevicesHelpText,
    setShowAssignedAlertDevicesHelpText,
  ] = useState(false);
  const assignedAlertDevicesHelpTextRef = useRef();
  function toggleAssignedAlertDevicesHelpText(value) {
    setShowAssignedAlertDevicesHelpText(value);
  }

  const assignedAlertDevicesHelpText = (
    <FormattedMessage
      id="userDisplay.assignedAlertDevicesHelpText"
      defaultMessage="These are alert devices that have been assigned to this unit."
      description="Help text explaining who the assigned users are"
    />
  );

  const [
    showQuickEditAlertDevicesHelpText,
    setShowQuickEditAlertDevicesHelpText,
  ] = useState(false);
  const quickEditAlertDevicesHelpTextRef = useRef();
  function toggleQuickEditAlertDevicesHelpText(value) {
    setShowQuickEditAlertDevicesHelpText(value);
  }

  const quickEditAlertDevicesHelpText = (
    <FormattedMessage
      id="userDisplay.quickEditAlertDevicesHelpText"
      defaultMessage="Clicking this button will take you to the interface where you can add or remove alert devices assigned to this user."
      description="Help text for understanding what the quick edit devices interface is"
    />
  );

  const [
    showAssignedAlarmDevicesHelpText,
    setShowAssignedAlarmDevicesHelpText,
  ] = useState(false);
  const assignedAlarmDevicesHelpTextRef = useRef();
  function toggleAssignedAlarmDevicesHelpText(value) {
    setShowAssignedAlarmDevicesHelpText(value);
  }

  const assignedAlarmDevicesHelpText = (
    <FormattedMessage
      id="userDisplay.assignedAlarmDevicesHelpText"
      defaultMessage="These are monitored devices that have been assigned to this user."
      description="Help text explaining who the assigned users are"
    />
  );

  const [
    showQuickEditAlarmDevicesHelpText,
    setShowQuickEditAlarmDevicesHelpText,
  ] = useState(false);
  const quickEditAlarmDevicesHelpTextRef = useRef();
  function toggleQuickEditAlarmDevicesHelpText(value) {
    setShowQuickEditAlarmDevicesHelpText(value);
  }

  const quickEditAlarmDevicesHelpText = (
    <FormattedMessage
      id="userDisplay.quickEditAlarmDevicesHelpText"
      defaultMessage="Clicking this button will take you to the interface where you can add or remove monitored devices assigned to this user."
      description="Help text for understanding what the quick edit devices interface is"
    />
  );

  const alertBatteryLowRef = useRef();
  const alarmBatteryLowRef = useRef();
  const alertBatteryRef = useRef();
  const alarmBatteryRef = useRef();

  /**
   * Function to generate the units table
   * @returns {object} - The table of units
   */
  const alertDevicesTable = () => {
    const alertDeviceDetailsList = [];
    alertDeviceList.map((details) => {
      let batteryIcon = (
        <div className="align-center height-100 flex-end text-success text-extra-large">
          <SvgBatteryOk
            className="cursor-pointer margin-right-10"
            onClick={() => alertBatteryRef.current.toggleHelpText()}
          />
        </div>
      );
      if (details.battery_low) {
        batteryIcon = (
          <div className="align-center height-100 flex-end text-alert text-extra-large">
            <SvgBatteryAlert
              className="cursor-pointer margin-right-10"
              onClick={() => alertBatteryLowRef.current.toggleHelpText()}
            />
          </div>
        );
      }

      let deviceLink = (
        <AccessControl
          data-test="access-control"
          feature={permissions.static.devices.alert.editAlertDevice}>
          <FormattedMessage
            id="userDisplay.viewAlertDeviceText"
            defaultMessage="View Device"
            description="Button to view full unit info">
            {(txt) => (
              <div className="width-100 flex-end height-100 align-center">
                <a
                  data-test="view-alert-device-link"
                  className="text-small margin-right-10 link-text align-center"
                  href={`/admin/alert_dev_edit.php?&alert_device_options=&mode=edit&edit_row[alert_device_id]=${details.alert_device_id}&edit_row[alert_device_type]=${details.alert_device_type}`}>
                  {txt} <SvgOpenExternal className="margin-left-5" />
                </a>
              </div>
            )}
          </FormattedMessage>
        </AccessControl>
      );
      if (
        details.alert_device_type === 3 ||
        details.alert_device_type === 4 ||
        details.alert_device_type === 24 ||
        details.alert_device_type === 25
      ) {
        deviceLink = (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.devices.alert.editAlertDevice}>
            <FormattedMessage
              id="userDisplay.viewAlertDeviceLink"
              defaultMessage="View Device"
              description="Button to view full unit info">
              {(txt) => (
                <div className="width-100 flex-end height-100 align-center">
                  <Link
                    data-test="view-alert-device-link-typed"
                    to={`/devices/alert/${details.alert_device_id}/view/`}
                    className="text-small margin-right-10 link-text align-center">
                    {txt} <SvgOpenExternal className="margin-left-5" />
                  </Link>
                </div>
              )}
            </FormattedMessage>
          </AccessControl>
        );
      }

      alertDeviceDetailsList.push({
        id: details.alert_device_id,
        description: details.alert_description,
        battery: batteryIcon,
        type: details.alert_device_type,
        view: deviceLink,
      });

      return '';
    });

    // map over the units and put into the format that is required for the table component
    if (alertDeviceDetailsList.length > 0) {
      const devicesArray = alertDeviceDetailsList.map((d) => {
        let deviceType = '';
        if (alertDeviceTypes[d.type]) {
          deviceType = alertDeviceTypes[d.type].description;
        }
        return {
          description: {
            sortBy: d.description.toUpperCase(),
            display: d.description,
          },
          type: {
            sortBy: deviceType.toUpperCase(),
            display: deviceType,
          },
          // battery: {
          //   sortBy: "",
          //   display: d.battery,
          // },
          view: {
            sortBy: '',
            display: d.view,
          },
        };
      });
      // Take the array and filter based on the text entered in the search bar.

      const tableHeadings = [
        {
          isSortable: true,
          sortBy: 'description',
          display: descriptionText,
        },
        {
          isSortable: true,
          sortBy: 'type',
          display: typeText,
        },
        // {
        //   isSortable: false,
        //   sortBy: "battery",
        //   display: batteryStatusText,
        // },
        {
          isSortable: false,
          sortBy: 'view',
          display: '',
        },
      ];

      // generates the actual table
      const table = (
        <Table
          hasTableHead={false}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="description"
          tableData={devicesArray}
          selectorType={selectorTypes.checkbox}
          tableStyles="table-layout__3-column"
          data-test="associated-alert-devices-table"
        />
      );
      return table;
    } else {
      return (
        <span className="text-dimmed margin-right-10">
          <FormattedMessage
            data-test="no-associated-alert-devices"
            id="userDevices.noAlertDevicesText"
            defaultMessage="No Alert Devices Assigned"
            description="The user's contact phone number"
          />
        </span>
      );
    }
  };

  /**
   * Function to generate the units table
   * @returns {object} - The table of units
   */
  const alarmDevicesTable = () => {
    const alarmDeviceDetailsList = [];
    alarmDeviceList.map((details) => {
      let batteryIcon = (
        <div className="align-center height-100 flex-end text-success text-extra-large"></div>
      );
      if (details.hardware_type === 2) {
        batteryIcon = (
          <div className="align-center height-100 flex-end text-success text-extra-large">
            <SvgBatteryOk
              className="cursor-pointer margin-right-10"
              onClick={() => alarmBatteryRef.current.toggleHelpText()}
            />
          </div>
        );
        if (details.battery_low) {
          batteryIcon = (
            <div className="align-center height-100 flex-end text-alert text-extra-large">
              <SvgBatteryAlert
                className="cursor-pointer margin-right-10"
                onClick={() => alarmBatteryLowRef.current.toggleHelpText()}
              />
            </div>
          );
        }
      }

      const deviceLink = (
        <AccessControl
          data-test="access-control"
          feature={permissions.static.devices.alarm.editAlarmDevice}>
          {userObj.is_active ? (
            <FormattedMessage
              id="userDisplay.viewDeviceText"
              defaultMessage="View Device"
              description="Button to view full unit info">
              {(txt) => (
                <div className="width-100 flex-end height-100 align-center">
                  <a
                    data-test="view-alarm-device-link"
                    className="text-small margin-right-10 link-text align-center"
                    href={`/admin/alarm_dev_edit_new.php?edit_row[alarm_device_id]=${details.alarm_device_id}&edit_row[alarm_device_type]=${details.alarm_device_type}&orig_type= 1`}>
                    {txt} <SvgOpenExternal className="margin-left-5" />
                  </a>
                </div>
              )}
            </FormattedMessage>
          ) : (
            ''
          )}
        </AccessControl>
      );

      alarmDeviceDetailsList.push({
        id: details.alarm_device_id,
        description: details.alarm_description,
        battery: batteryIcon,
        type: details.alarm_device_type,
        view: deviceLink,
      });

      return '';
    });

    // map over the units and put into the format that is required for the table component
    if (alarmDeviceDetailsList.length > 0) {
      const devicesArray = alarmDeviceDetailsList.map((d) => {
        let deviceType = '';
        if (alarmDeviceTypes[d.type]) {
          deviceType = alarmDeviceTypes[d.type].description;
        }
        return {
          description: {
            sortBy: d.description.toUpperCase(),
            display: d.description,
          },
          type: {
            sortBy: deviceType.toUpperCase(),
            display: deviceType,
          },
          // battery: {
          //   sortBy: "",
          //   display: d.battery,
          // },
          view: {
            sortBy: '',
            display: d.view,
          },
        };
      });
      // Take the array and filter based on the text entered in the search bar.

      const tableHeadings = [
        {
          isSortable: true,
          sortBy: 'description',
          display: descriptionText,
        },
        {
          isSortable: true,
          sortBy: 'type',
          display: typeText,
        },
        // {
        //   isSortable: false,
        //   sortBy: "battery",
        //   display: batteryStatusText,
        // },
        {
          isSortable: false,
          sortBy: 'view',
          display: '',
        },
      ];

      // generates the actual table
      const table = (
        <Table
          hasTableHead={false}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="description"
          tableData={devicesArray}
          selectorType={selectorTypes.checkbox}
          tableStyles="table-layout__3-column"
          data-test="associated-alarm-devices-table"
        />
      );
      return table;
    } else {
      return (
        <span className="text-dimmed margin-right-10">
          <FormattedMessage
            data-test="no-associated-alarm-devices"
            id="userDevices.noAlarmDevicesText"
            defaultMessage="No Monitored Devices Assigned"
            description="The user's contact phone number"
          />
        </span>
      );
    }
  };

  return (
    <div className="content-display__container" data-test="content-container">
      <div
        className="content-display__column"
        data-test="alert-devices-container">
        <div className="content-display__item-pill-container">
          <div className="item-container__main-container">
            <div className="item-container__main-text text-large text-light flex-spread margin-bottom-10">
              <div className="align-center">
                <FormattedMessage
                  id="userDevices.assignedAlertDevicesText"
                  defaultMessage="Assigned Alert Devices:"
                  description="The assigned alert devices for this unit"
                />
                <HelpIcon
                  onClick={() =>
                    assignedAlertDevicesHelpTextRef.current.toggleHelpText()
                  }
                  toggled={showAssignedAlertDevicesHelpText}
                  styles="margin-left-5"
                />
              </div>
              <AccessControl
                data-test="access-control"
                feature={permissions.static.devices.editDeviceAssociation}>
                {userObj.is_active ? (
                  <div className="align-center">
                    <Link
                      data-test="associate-alert-device"
                      className="link-button button-secondary button-xsm text-small link-secondary"
                      to={`/users/${id}/edit/?mode=associateAlertDevice`}
                      state={{
                        modal: true,
                        previous: currentLocation,
                      }}
                    >
                      {quickEditAlertDevicesText}
                    </Link>
                    <HelpIcon
                      onClick={() =>
                        quickEditAlertDevicesHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showQuickEditAlertDevicesHelpText}
                      styles="margin-left-5"
                    />
                  </div>
                ) : (
                  ''
                )}
              </AccessControl>
            </div>
            <div className="item-container__secondary-text text-medium text-bold flex-row align-center">
              {alertDevicesTable()}
            </div>
          </div>
          <div className="item-container__info-container"></div>
        </div>
        <InlineHelpText
          ref={assignedAlertDevicesHelpTextRef}
          toggleFunction={toggleAssignedAlertDevicesHelpText}
          message={assignedAlertDevicesHelpText}
        />
        <InlineHelpText
          ref={quickEditAlertDevicesHelpTextRef}
          toggleFunction={toggleQuickEditAlertDevicesHelpText}
          message={quickEditAlertDevicesHelpText}
        />
        <InlineWarningText
          ref={alertBatteryLowRef}
          message={batteryLowHelpText}
        />
        <InlineHelpText ref={alertBatteryRef} message={batteryHelpText} />
      </div>
      <div
        className="content-display__column"
        data-test="alarm-devices-container">
        <div className="content-display__item-pill-container">
          <div className="item-container__main-container">
            <div className="item-container__main-text text-large text-light flex-spread margin-bottom-10">
              <div className="align-center">
                <FormattedMessage
                  id="userDevices.assignedAlarmDevicesText"
                  defaultMessage="Assigned Monitored Devices:"
                  description="The assigned alarm devices for this unit"
                />
                <HelpIcon
                  onClick={() =>
                    assignedAlarmDevicesHelpTextRef.current.toggleHelpText()
                  }
                  toggled={showAssignedAlarmDevicesHelpText}
                  styles="margin-left-5"
                />
              </div>
              <AccessControl
                data-test="access-control"
                feature={permissions.static.devices.editDeviceAssociation}>
                {userObj.is_active ? (
                  <div className="align-center">
                    <Link
                      data-test="associate-alarm-device"
                      className="link-button button-secondary button-xsm text-small link-secondary"
                      to={`/users/${id}/edit/?mode=associateAlarmDevice`}
                      state={{
                        modal: true,
                        previous: currentLocation,
                      }}>
                      {quickEditAlarmDevicesText}
                    </Link>
                    <HelpIcon
                      onClick={() =>
                        quickEditAlarmDevicesHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showQuickEditAlarmDevicesHelpText}
                      styles="margin-left-5"
                    />
                  </div>
                ) : (
                  ''
                )}
              </AccessControl>
            </div>
            <div className="item-container__secondary-text text-medium text-bold flex-row align-center">
              {alarmDevicesTable()}
            </div>
          </div>
          <div className="item-container__info-container"></div>
        </div>
        <InlineHelpText
          ref={assignedAlarmDevicesHelpTextRef}
          toggleFunction={toggleAssignedAlarmDevicesHelpText}
          message={assignedAlarmDevicesHelpText}
        />
        <InlineHelpText
          ref={quickEditAlarmDevicesHelpTextRef}
          toggleFunction={toggleQuickEditAlarmDevicesHelpText}
          message={quickEditAlarmDevicesHelpText}
        />
        <InlineWarningText
          ref={alarmBatteryLowRef}
          message={batteryLowHelpText}
        />
        <InlineHelpText ref={alarmBatteryRef} message={batteryHelpText} />
      </div>
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    alarmDeviceTypes: state.devices.alarmDeviceTypes,
    alertDeviceTypes: state.devices.alertDeviceTypes,
  };
};

export default connect(mapStateToProps)(UnconnectedUserDevices);
